/**
 * Background music: "Toy Piano Welcome" on the menu, notebook, creator and
 * other calm pages, "The Sneaky Boss" in every battle (story, Hall of Fame,
 * dares, boss and online fights). Two looping <audio> tracks with a short
 * crossfade; nothing downloads until a track is needed.
 *
 * The volume goes through a WebAudio gain per track, because iPhones ignore
 * <audio>.volume. Browsers only start sound after the player touches the
 * page, so the wanted track begins on the first tap or key. Music pauses
 * while the tab is hidden, and the battle track starts from the top of every
 * battle.
 */
export type MusicTrack = 'lobby' | 'battle';

const SOURCES: Record<MusicTrack, string> = {
  lobby: 'music/toy-piano-welcome.mp3',
  battle: 'music/the-sneaky-boss.mp3',
};

/** Crossfade time constant (seconds) and how long until a faded track is paused (ms). */
const FADE_S = 0.25;
const PAUSE_AFTER_MS = 1200;
/** Music sits under the sound effects. */
const MIX = 0.55;

interface Channel {
  el: HTMLAudioElement;
  gain: GainNode | null;
  pauseTimer: number;
}

export class Music {
  private ctx: AudioContext | null = null;
  private channels = new Map<MusicTrack, Channel>();
  private wanted: MusicTrack | null = null;
  private volume = 0.5;
  private muted = false;
  private unlocked = false;

  constructor(private readonly base = `${import.meta.env.BASE_URL}assets/`) {
    const unlock = (): void => {
      this.unlocked = true;
      void this.ctx?.resume();
      this.apply();
    };
    for (const ev of ['pointerdown', 'touchend', 'click', 'keydown'] as const) window.addEventListener(ev, unlock, { passive: true, capture: true });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') for (const c of this.channels.values()) c.el.pause();
      else this.apply();
    });
  }

  /** Music volume 0–1; `muted` is the game's "Mute sounds" switch. */
  setVolume(volume: number, muted: boolean): void {
    this.volume = Math.max(0, Math.min(1, volume));
    this.muted = muted;
    this.apply();
  }

  /** The track a scene should hear. */
  static forScene(name: string): MusicTrack {
    return name === 'battle' ? 'battle' : 'lobby';
  }

  play(track: MusicTrack): void {
    if (this.wanted === track) return;
    this.wanted = track;
    // Every battle starts its music from the top; the calm music carries on where it was.
    const c = this.channels.get(track);
    if (c && track === 'battle') c.el.currentTime = 0;
    this.apply();
  }

  private level(): number {
    return this.muted ? 0 : this.volume * MIX;
  }

  private channel(track: MusicTrack): Channel {
    let c = this.channels.get(track);
    if (c) return c;
    const el = new Audio(this.base + SOURCES[track]);
    el.loop = true;
    el.preload = 'auto';
    let gain: GainNode | null = null;
    try {
      const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (Ctor) {
        this.ctx ??= new Ctor();
        gain = this.ctx.createGain();
        gain.gain.value = 0;
        this.ctx.createMediaElementSource(el).connect(gain).connect(this.ctx.destination);
      }
    } catch {
      gain = null; // plain <audio> volume then
    }
    if (!gain) el.volume = 0;
    c = { el, gain, pauseTimer: 0 };
    this.channels.set(track, c);
    return c;
  }

  private setLevel(c: Channel, to: number): void {
    if (c.gain && this.ctx) c.gain.gain.setTargetAtTime(to, this.ctx.currentTime, FADE_S);
    else c.el.volume = to;
  }

  /** The wanted track fades in (starting it if needed); every other one fades out and pauses. */
  private apply(): void {
    if (!this.unlocked || !this.wanted || document.visibilityState === 'hidden') return;
    void this.ctx?.resume();
    for (const [track, c] of this.channels) {
      if (track === this.wanted) continue;
      this.setLevel(c, 0);
      window.clearTimeout(c.pauseTimer);
      c.pauseTimer = window.setTimeout(() => c.el.pause(), PAUSE_AFTER_MS);
    }
    const c = this.channel(this.wanted);
    window.clearTimeout(c.pauseTimer);
    const level = this.level();
    if (level <= 0) {
      this.setLevel(c, 0);
      c.pauseTimer = window.setTimeout(() => c.el.pause(), PAUSE_AFTER_MS);
      return;
    }
    if (c.el.paused) {
      c.el.play().catch((err: unknown) => {
        // Not allowed yet (no gesture): try again on the next tap. A play cut short by a pause is fine.
        if (err instanceof DOMException && err.name === 'NotAllowedError') this.unlocked = false;
      });
    }
    this.setLevel(c, level);
  }
}
